import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getBookingPage, updateTab } from "../api/bookingPageApi";

export const useBookingPage = () => {
  return useQuery({
    queryKey: ["bookingPage"],
    queryFn: getBookingPage,
  });
};

export const useUpdateBookingPageTab = (tab, options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => updateTab(tab, data),
    onMutate: async (newData) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["bookingPage"], exact: true });

      // Snapshot previous value
      const previousData = queryClient.getQueryData(["bookingPage"]);

      // Optimistically update - merge with the data property
      queryClient.setQueryData(["bookingPage"], (old) => {
        if (!old?.data) return old;
        return {
          ...old,
          data: {
            ...old.data,
            ...newData,
          },
        };
      });

      return { previousData };
    },
    onError: (err, newData, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData(["bookingPage"], context.previousData);
      }
    },
    onSuccess: (data, variables, context) => {
      // Call custom onSuccess callback if provided
      if (options.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
    onSettled: () => {
      // Refetch to ensure sync
      queryClient.invalidateQueries({ queryKey: ["bookingPage"], exact: true });
    },
  });
};

// Convenience hook for general tab (currently the only one used)
export const useUpdateGeneralTab = () => useUpdateBookingPageTab("general");

// Convenience hook for branding tab
export const useUpdateBrandingTab = () => useUpdateBookingPageTab("branding");

// Convenience hook for settings tab
export const useUpdateSettingsTab = () => useUpdateBookingPageTab("settings");

// Convenience hook for policies tab
export const useUpdatePoliciesTab = () => useUpdateBookingPageTab("policies");
